// @flow
import { makeStyles, Theme, withTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import DialogContent from '@material-ui/core/DialogContent';
import ScribeFast from '../../../assets/icons/scribe-fast.svg';
import OkIcon from '../../../assets/icons/ok.svg';
import ButtonBase from '@material-ui/core/ButtonBase';
import ScribeAccurate from '../../../assets/icons/scribe-accurate.svg';
import React from 'react';
import { withSnackbar } from 'notistack';
import { withTranslation } from 'react-i18next';
import { setScribeCorrectionMode } from '../../actions/config';
import { ScribeCorrectionModeAccurate, ScribeCorrectionModeFast } from '../../types/config';
import { useDispatch, useSelector } from 'react-redux';
import { getRecording } from '../../selectors';
import { selectCorrectionMode } from '../../selectors/scribe-transcript-ui';

const useStyles = makeStyles(theme => ({
  chooserTitle: {
    color: theme.palette.ava.blue,
  },
  title: {
    fontWeight: 'bold',
    fontSize: 30,
    textAlign: 'center',
    paddingTop: 16,
  },
  scribeCorrectionTitle: {
    fontWeight: 500,
  },
  button: {
    '&:hover': {
      opacity: 0.9,
    },
    width: 265,
    height: 55,
    flexDirection: 'column',
    padding: 4,
    color: 'white',
    fontWeight: 600,
    fontSize: 16,
    borderRadius: 24.8,
    border: '1px solid',
    boxShadow: '-8.928px -8.928px 14.88px rgba(100, 218, 255, 0.160784)',
  },
  scribeCorrectionButton: {
    background: theme.palette.ava.green2,
    fontWeight: 500,
    width: '100%',
  },
  green2: {
    color: theme.palette.ava.green2,
  },
  green2Background: {
    backgroundColor: theme.palette.ava.green2,
  },
  scribeCorrectionModeOptionBox: {
    border: '1px solid #DADCE0',
    marginTop: '12px',
    marginBottom: '35px',
    borderRadius: '15px',
    padding: '20px',
    '&.modeSelected': {
      borderColor: theme.palette.ava.green2,
      backgroundColor: theme.palette.ava.navy5,
      border: '4px solid',
    },
  },
  scribeCorrectionModeIcon: {
    marginRight: '10px',
    verticalAlign: 'baseline',
    height: '0.7em',
  },
  checkboxIcon: {
    backgroundColor: theme.palette.ava.green2,
    borderRadius: '100px',
    marginLeft: '10px',
    height: '0.8em',
  },
  scribeCorrectionModeProgressRow: {
    alignItems: 'end',
    fontWeight: 500,
  },
  scribeCorrectionModeProgressTiny: {
    textAlign: 'end',
    fontSize: 10,
  },
  scribeCorrectionModeProgressBar: {
    width: '100%',
    height: '0.5em',
    borderRadius: '15px',
    backgroundColor: theme.palette.ava.green2,
  },
  divider: {
    width: '100%',
    borderTop: '1px solid #DADCE0',
    margin: '10px 0px',
  },
  currentSelectionText: {
    color: theme.palette.ava.green2,
    textAlign: 'center',
    width: '100%',
    fontSize: '18px',
    height: 55,
    lineHeight: '55px',
  },
}));

type ScribeCorrectionModeBoxProps = {
  t: Function,
  theme: Theme,
  onClose: Function,
};

function ScribeCorrectionModeBox(props: ScribeCorrectionModeBoxProps) {
  const { t, theme, onClose } = props;
  const dispatch = useDispatch();
  const classes = useStyles(theme);
  const currentCorrectionMode = useSelector(selectCorrectionMode);
  const addTitleClass = (klass: string) => `${classes.title} ${klass}`;
  const addButtonClass = (klass: string) => `${classes.button} ${klass}`;

  const selectFastMode = () => {
    dispatch(setScribeCorrectionMode(ScribeCorrectionModeFast));
    onClose();
  };
  const selectAccurateMode = () => {
    dispatch(setScribeCorrectionMode(ScribeCorrectionModeAccurate));
    onClose();
  };

  const makeProgressBarRow = (name, label, width) => {
    return (
      <Grid item style={{ width: '100%' }} className={classes.green2}>
        <Grid container direction="row" className={classes.scribeCorrectionModeProgressRow}>
          <Grid item xs={3} style={{ fontSize: 12 }}>
            {name}
          </Grid>
          <Grid item xs={9}>
            <Grid container direction="column" style={{ width: width, paddingBottom: 5 }}>
              <Grid item className={classes.scribeCorrectionModeProgressTiny}>
                {label}
              </Grid>
              <Grid item className={classes.scribeCorrectionModeProgressBar} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };
  const makeScribeCorrectionOption = (
    selected,
    name,
    icon,
    speedNum,
    speedName,
    accuracyNum,
    accuracyName,
    desc,
    onClick
  ) => {
    return (
      <Grid
        item
        xs={12}
        md={5}
        className={`${classes.scribeCorrectionModeOptionBox} ${selected ? 'modeSelected' : ''}`}
      >
        <Grid
          container
          direction="column"
          justify="flex-start"
          alignItems="flex-start"
          style={{ height: '100%', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" className={classes.scribeCorrectionTitle}>
            <img className={classes.scribeCorrectionModeIcon} src={icon} alt="Scribe Correction Option" />
            {name}
            {selected && (
              <img
                className={`${classes.scribeCorrectionModeIcon} ${classes.checkboxIcon}`}
                src={OkIcon}
                alt="selected"
              />
            )}
          </Typography>
          {makeProgressBarRow(t('scribeCorrectionMode.speed'), speedName, `${speedNum}%`)}
          {makeProgressBarRow(t('scribeCorrectionMode.accuracy'), accuracyName, `${accuracyNum}%`)}
          <Box my={3}>
            <span>{t('scribeCorrectionMode.descBegin')} </span>
            <span style={{ fontWeight: 'bold' }}>{desc}</span>
          </Box>
          <div className={classes.divider} />
          {!selected && (
            <ButtonBase
              component="div"
              className={addButtonClass(classes.scribeCorrectionButton)}
              focusRipple
              onClick={onClick}
            >
              <Box textAlign="center" whiteSpace="nowrap">
                {t('scribeCorrectionMode.select')}
              </Box>
            </ButtonBase>
          )}
          {selected && <Box className={classes.currentSelectionText}>{t('scribeCorrectionMode.currentSelection')}</Box>}
        </Grid>
      </Grid>
    );
  };
  return (
    <Box style={{ maxWidth: 750 }}>
      <DialogTitle disableTypography>
        <Typography className={addTitleClass(classes.chooserTitle)} variant="h5">
          <Box px={4}>{t('scribeCorrectionMode.title')}</Box>
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container direction="row" style={{ justifyContent: 'space-evenly' }}>
          {makeScribeCorrectionOption(
            currentCorrectionMode === ScribeCorrectionModeFast,
            t('scribeCorrectionMode.fastMode'),
            ScribeFast,
            '100',
            t('scribeCorrectionMode.fastSpeed'),
            '75',
            t('scribeCorrectionMode.fastAccuracy'),
            t('scribeCorrectionMode.fastDesc'),
            selectFastMode
          )}
          {makeScribeCorrectionOption(
            currentCorrectionMode === ScribeCorrectionModeAccurate,
            t('scribeCorrectionMode.accurateMode'),
            ScribeAccurate,
            '70',
            t('scribeCorrectionMode.accurateSpeed'),
            '100',
            t('scribeCorrectionMode.accurateAccuracy'),
            t('scribeCorrectionMode.accurateDesc'),
            selectAccurateMode
          )}
        </Grid>
      </DialogContent>
    </Box>
  );
}

export default withTheme(withSnackbar(withTranslation()(ScribeCorrectionModeBox)));
